import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Button,
  Box,
  TextField,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import { db, auth } from "../../firebase-config";
import { collection, getDocs, query, where, doc, getDoc, addDoc, deleteDoc } from "firebase/firestore";

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [productCount, setProductCount] = useState(0);
  const [pendingQuotes, setPendingQuotes] = useState(0);
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const currentUser = auth.currentUser;

  useEffect(() => {
    if (!currentUser) return;

    const checkAdminStatus = async () => {
      try {
        const adminRef = doc(db, "admins", currentUser.uid);
        const adminSnap = await getDoc(adminRef);
        if (adminSnap.exists()) {
          setIsAdmin(true);
          fetchOverviewData();
          fetchCategories();
        } else {
          setIsAdmin(false);
          navigate("/");
        }
      } catch (err) {
        console.error("Error checking admin status:", err);
        setError("Failed to verify admin access.");
      } finally {
        setLoading(false);
      }
    };

    const fetchOverviewData = async () => {
      try {
        // Fetch total products count
        const productsRef = collection(db, "products");
        const productsSnapshot = await getDocs(productsRef);
        setProductCount(productsSnapshot.size);

        // Fetch pending quotes count
        const quotesRef = collection(db, "quotes");
        const q = query(quotesRef, where("status", "==", "pending"));
        const quotesSnapshot = await getDocs(q);
        setPendingQuotes(quotesSnapshot.size);
      } catch (err) {
        console.error("Error fetching overview data:", err);
        setError("Failed to load overview data.");
      }
    };

    checkAdminStatus();
  }, [currentUser, navigate]);

  // Fetch Categories from Firestore
  const fetchCategories = async () => {
    try {
      const categoriesRef = collection(db, "Categories");
      const categoriesSnap = await getDocs(categoriesRef);
      const categoryList = categoriesSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCategories(categoryList);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  // Add a new category
  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;

    try {
      const categoriesRef = collection(db, "Categories");
      const docRef = await addDoc(categoriesRef, { name: newCategory.trim() });
      setCategories([...categories, { id: docRef.id, name: newCategory.trim() }]);
      setNewCategory(""); // Clear input
    } catch (err) {
      console.error("Error adding category:", err);
    }
  };

  // Delete a category
  const handleDeleteCategory = async (categoryId) => {
    try {
      await deleteDoc(doc(db, "Categories", categoryId));
      setCategories(categories.filter((category) => category.id !== categoryId));
    } catch (err) {
      console.error("Error deleting category:", err);
    }
  };

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Admin Dashboard
      </Typography>

      <Grid container spacing={3}>
        {/* Products Overview */}
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography variant="h6">Total Products</Typography>
              <Typography variant="h4">{productCount}</Typography>
              <Button
                component={Link}
                to="/admin/products"
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
              >
                Manage Products
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Quotes Overview */}
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography variant="h6">Pending Quotes</Typography>
              <Typography variant="h4">{pendingQuotes}</Typography>
              <Button
                component={Link}
                to="/admin/quotes"
                variant="contained"
                sx={{ mt: 2 }}
              >
                Manage Quotes
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Links */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5">Quick Links</Typography>
        <Button
          component={Link}
          to="/admin/products"
          variant="contained"
          sx={{ mt: 1, mr: 2 }}
        >
          Product Management
        </Button>
        <Button
          component={Link}
          to="/admin/projects"
          variant="contained"
          sx={{ mt: 1, mr: 2 }}
        >
          Project Management
        </Button>

        <Button
          component={Link}
          to="/admin/quotes"
          variant="contained"
          sx={{ mt: 1, mr: 2 }}
        >
          Quote Management
        </Button>
        <Button
          component={Link}
          to="/admin/companies"
          variant="contained"
          sx={{ mt: 1, mr: 2 }}
        >
          Companies Management
        </Button>
        <Button
          component={Link}
          to="/admin/users"
          variant="contained"
          sx={{ mt: 1, mr: 2 }}
        >
          Users Management
        </Button>
      </Box>

      {/* Category Management */}
      <Box sx={{ mt: 5 }}>
        <Typography variant="h5">Manage Categories</Typography>

        {/* Add Category */}
        <Box sx={{ display: "flex", mt: 2, gap: 2 }}>
          <TextField
            label="New Category"
            variant="outlined"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            fullWidth
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddCategory}
          >
            Add
          </Button>
        </Box>

        {/* Category List */}
        <List sx={{ mt: 2 }}>
          {categories.map((category) => (
            <ListItem key={category.id} secondaryAction={
              <IconButton
                edge="end"
                color="error"
                onClick={() => handleDeleteCategory(category.id)}
              >
                <DeleteIcon />
              </IconButton>
            }>
              <ListItemText primary={category.name} />
            </ListItem>
          ))}
        </List>
      </Box>
    </Container>
  );
};

export default AdminDashboard;
